<template>
  <Layout
    class="onboarding-book-selection-20710"
    :title="page.title"
    :subtitle="page.subtitle"
    :scrollDefaultSlot="true"
  >
    <form class="onboarding-book-selection-20710__search" @submit.prevent>
      <span
        class="onboarding-book-selection-20710__search-icon iconfont isearch"
        aria-hidden="true"
      ></span>
      <input
        v-model="term"
        class="onboarding-book-selection-20710__input"
        type="search"
        placeholder="Search any book or an idea"
        aria-label="Search any book or an idea"
        :aria-busy="isSearching"
        autocomplete="off"
      />
      <CircleSpinner
        v-if="isSearching"
        class="onboarding-book-selection-20710__spinner"
        :size="18"
        color="#6b6d76"
      />
    </form>

    <div class="onboarding-book-selection-20710__results">
      <p
        v-if="showEmpty"
        class="onboarding-book-selection-20710__empty"
        role="status"
      >
        <img
          class="onboarding-book-selection-20710__empty-icon"
          src="@/assets.app/images/onboarding/book-search-empty.svg"
          alt=""
        />
        <span class="onboarding-book-selection-20710__empty-copy">
          <span class="onboarding-book-selection-20710__empty-title">
            No results found.
          </span>
          <span class="onboarding-book-selection-20710__empty-hint">
            Try a new search, or check the spelling.
          </span>
        </span>
      </p>

      <ul v-else class="onboarding-book-selection-20710__options">
        <li v-for="book in books" :key="book.url_slug">
          <button
            type="button"
            class="onboarding-book-selection-20710__option"
            :class="{
              'onboarding-book-selection-20710__option--selected':
                isSelected(book),
            }"
            :aria-label="`${book.title} by ${book.author}`"
            :aria-pressed="isSelected(book)"
            @click="selectBook(book)"
          >
            <BookCardThumbnail
              class="onboarding-book-selection-20710__thumb"
              :doc="book"
              coverClass="onboarding-book-selection-20710__cover"
              fallbackCoverClass="onboarding-book-selection-20710__fallback"
              cacheBust
            />
            <span
              class="onboarding-book-selection-20710__radio"
              aria-hidden="true"
            ></span>
          </button>
        </li>
      </ul>
    </div>

    <div class="onboarding-book-selection-20710__footer">
      <AppButton
        class="onboarding-book-selection-20710__continue"
        :disabled="!selectedSlug"
        :loading="view.isLoadingNextPage"
        @click="view.continue()"
      >
        Build my path
      </AppButton>
    </div>
  </Layout>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, ref, watch } from 'vue'

import { Mixpanel } from '@/analytics/mixpanel'
import { debounce } from '@/helpers/form'
import { cacheBust } from '@/models/doc'
import type { QuestionnaireView } from '@/models/onboarding'
import {
  INITIAL_BOOKS,
  sampleGuideFromDoc,
  SEARCH_DEBOUNCE_MS,
  SEARCH_FETCH_LIMIT,
} from '@/models/onboarding.book-selection-20710'
import type {
  BookSelection20710Page,
  SampleGuideChoice20710,
} from '@/models/onboarding.types'
import { backend } from '@/services/backend'
import { Sentry } from '@/services/sentry'

import CircleSpinner from '@/components/ui/CircleSpinner.vue'
import AppButton from '@/app/components/button/AppButton.vue'
import BookCardThumbnail from '@/app/components/book/card/BookCardThumbnail.vue'
import Layout from '../Layout.vue'

const props = defineProps<{
  view: QuestionnaireView
}>()

const page = computed(() => props.view.currentPage as BookSelection20710Page)

const term = ref('')
const books = ref<SampleGuideChoice20710[]>([...INITIAL_BOOKS])
const selectedSlug = ref<string | null>(null)
const isSearching = ref(false)
const query = computed(() => term.value.trim())

const showEmpty = computed(
  () => query.value !== '' && !isSearching.value && books.value.length === 0,
)

const debouncedSearch = debounce(() => {
  void runSearch()
}, SEARCH_DEBOUNCE_MS)

watch(term, () => {
  if (!query.value) {
    isSearching.value = false
    books.value = [...INITIAL_BOOKS]
    return
  }

  isSearching.value = true
  debouncedSearch()
})

function isSelected(book: SampleGuideChoice20710): boolean {
  return selectedSlug.value === book.url_slug
}

function selectBook(book: SampleGuideChoice20710): void {
  selectedSlug.value = book.url_slug
  props.view.setCurrentChoice(book)
}

async function runSearch(): Promise<void> {
  const submitted = query.value
  if (!submitted) {
    return
  }

  try {
    const result = await backend.getDocSearch(
      'book',
      submitted,
      SEARCH_FETCH_LIMIT,
      0,
    )
    if (query.value !== submitted) {
      return
    }

    const docs = result.data ?? []
    books.value = docs.map(sampleGuideFromDoc)
    if (docs.length === 0) {
      Mixpanel.trackOnboardingBookSearchNoResults20710(submitted)
    }
  } catch (error) {
    if (query.value !== submitted) {
      return
    }
    Sentry.captureException(error)
  } finally {
    if (query.value === submitted) {
      isSearching.value = false
    }
  }
}

onBeforeMount(() => {
  for (const book of INITIAL_BOOKS) {
    if (!book.cover_image) {
      continue
    }
    const image = new Image()
    image.src = cacheBust(book.cover_image)
  }
})
</script>

<style lang="scss" scoped>
.onboarding-book-selection-20710.onboarding-layout--scroll-default-slot {
  align-items: stretch;
  overflow: hidden;

  :deep(.onboarding-layout__default-slot) {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    flex: 1;
    height: auto;
    min-height: 0;
    overflow: hidden;
    padding-bottom: var(--onboarding-padding);
  }

  :deep(.onboarding-layout__default-slot > *) {
    width: 100%;
    max-width: none;
    margin-inline: auto;

    @media (min-width: $w-pc-b) {
      max-width: 600px;
    }
  }
}

.onboarding-book-selection-20710 {
  :deep(.onboarding-layout__text) {
    gap: 10px;
    width: 100%;
    max-width: none;
    margin-inline: auto;
    margin-bottom: 24px;

    @media (min-width: $w-pc-b) {
      max-width: 600px;
    }
  }

  :deep(.onboarding-layout__subtitle) {
    margin-bottom: 0;
  }

  &__search {
    position: sticky;
    top: 0;
    z-index: 2;
    display: flex;
    flex: none;
    align-items: center;
    gap: 10px;
    height: 58px;
    margin-bottom: 24px;
    padding: 0 20px;
    border: 1px solid $c-black-13;
    border-radius: 12px;
    background: $c-white;

    &:focus-within {
      border-color: $c-yellow;
    }
  }

  &__search-icon {
    flex: none;
    color: $c-black-3;
    font-size: 19px;
  }

  &__input {
    flex: 1;
    min-width: 0;
    border: 0;
    outline: none;
    background: transparent;
    color: $c-black-2;
    font-size: 16px;
    line-height: 22px;
    letter-spacing: -0.32px;

    &::placeholder {
      color: $c-black-3;
    }

    &::-webkit-search-cancel-button {
      -webkit-appearance: none;
    }
  }

  &__spinner {
    flex: none;
  }

  &__results {
    flex: 1;
    min-height: 0;
    margin-bottom: -120px;
    overflow-x: hidden;
    overflow-y: auto;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  &__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    height: 100%;
    margin: 0;
    padding-bottom: 120px;
    text-align: center;
    gap: 20px;
  }

  &__empty-icon {
    display: block;
  }

  &__empty-copy {
    display: flex;
    flex-direction: column;
    align-items: center;
    max-width: 281px;
  }

  &__empty-title,
  &__empty-hint {
    font-size: 16px;
    line-height: 24px;
    letter-spacing: -0.32px;
  }

  &__empty-title {
    color: $c-black-2;
    font-weight: 500;
  }

  &__empty-hint {
    color: $c-black-3;
  }

  &__options {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px 11px;
    width: 100%;
    margin: 0;
    padding: 0 0 140px;
    list-style: none;

    @media (min-width: $w-pc-b) {
      grid-template-columns: repeat(4, 1fr);
      gap: 24px 20px;
    }

    &:has(.onboarding-book-selection-20710__option--selected)
      .onboarding-book-selection-20710__thumb {
      opacity: 0.7;
    }

    &:has(.onboarding-book-selection-20710__option--selected)
      .onboarding-book-selection-20710__option--selected
      .onboarding-book-selection-20710__thumb {
      opacity: 1;
    }
  }

  &__option {
    position: relative;
    display: block;
    width: 100%;
    padding: 0;
    border: 0;
    background: transparent;
    cursor: pointer;

    &:focus-visible {
      outline: 2px solid $c-yellow;
      outline-offset: 2px;
    }
  }

  &__thumb {
    display: block;
    width: calc(100% - 8px);
    margin-top: 7px;
    border: 1px solid $c-black-13;
    border-radius: 9% / 6.75%;
    overflow: hidden;
    container-type: inline-size;
    --cover-width: 100cqi;
    transition: opacity 0.2s ease;
  }

  :deep(.book-thumbnail__cover--background) {
    display: none;
  }

  :deep(.onboarding-book-selection-20710__cover) {
    display: block;
    width: 100%;
    height: auto;
    aspect-ratio: 3 / 4;
    object-fit: cover;
  }

  :deep(.onboarding-book-selection-20710__fallback) {
    width: 100%;
  }

  &__radio {
    position: absolute;
    z-index: 2;
    top: 0;
    right: 0;
    display: block;
    width: 22px;
    height: 22px;
    border: 1px solid $c-black-3;
    border-radius: 50%;
    background: $c-white;
  }

  &__option--selected &__radio {
    border: 1.5px solid $c-black-2;

    &::after {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      width: 11px;
      height: 11px;
      border-radius: 50%;
      background: $c-black-2;
      transform: translate(-50%, -50%);
    }
  }

  &__footer {
    position: relative;
    z-index: 2;
    flex: none;
    width: 100%;

    &::before {
      content: '';
      position: absolute;
      z-index: -1;
      left: 0;
      bottom: calc(var(--onboarding-padding) * -1);
      width: 100%;
      height: 200px;
      background: linear-gradient(
        to bottom,
        rgba($c-bg, 0) 0%,
        rgba($c-bg, 0.08) 45%,
        rgba($c-bg, 0.72) 78%,
        $c-bg 100%
      );
      pointer-events: none;
    }
  }

  &__continue {
    position: relative;
    z-index: 1;
    width: 100%;
    height: 56px;
    border: 0;
    border-radius: 50px;
    font-size: 18px;
    line-height: 22px;
    background-color: $c-yellow;
    color: $c-black-2;

    @include hover {
      background-color: $c-yellow-5;
      color: $c-black-2;
    }

    &.app-btn--disabled {
      opacity: 1;
      background-color: #dde1e5;
      color: #596174;

      @include hover {
        background-color: #dde1e5;
        color: #596174;
      }
    }
  }
}
</style>
